import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import logo from "../../../assets/agility-logo.png";
import {
  Box,
  Typography,
  Grid,
  Divider,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  Paper,
  TableBody,
  IconButton,
  Tooltip,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import api from "../../../services/api";
import "./ConsultationInvoice.css";

const ConsultationInvoice = () => {
  const [invoiceDetails, setInvoiceDetails] = useState({});
  const location = useLocation();

  const params = new URLSearchParams(location.search);
  const patient_id = params.get("patient_id");
  const service_id = params.get("service_id");

  useEffect(() => {
    if (patient_id && service_id) {
      generateInvoiceDetails(patient_id, service_id);
    }
  }, [patient_id, service_id]);

  const generateInvoiceDetails = async (patient_id, service_id) => {
    try {
      const response = await api.get(
        `app/invoice_management/patient_bill/?patient_id=${patient_id}&service_id=${service_id}`
      );

      if (response?.status === 200) {
        setInvoiceDetails(response?.data);
      }
    } catch (error) {
      console.error("Invoice fetch error:", error);
    }
  };

  const formatCurrency = (amount) =>
    new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
    }).format(amount || 0);


  const waitForImages = async (container) => {
    const imgs = container.querySelectorAll("img");
    await Promise.all(
      Array.from(imgs).map((img) => {
        if (img.complete) return Promise.resolve();
        return new Promise((resolve) => {
          img.onload = img.onerror = resolve;
        });
      })
    );
  };

  const convertImgToBase64 = async (url) => {
    try {
      const response = await fetch(url, { mode: "cors" });
      const blob = await response.blob();
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.readAsDataURL(blob);
      });
    } catch (err) {
      console.warn("Failed to convert image to base64:", err);
      return url; // fallback to original URL
    }
  };

  const handleDownload = async () => {
    if (invoiceDetails?.organisation_logo) {
      const base64Logo = await convertImgToBase64(
        invoiceDetails.organisation_logo
      );
      const logoImg = document.getElementById("invoiceLogo");
      if (logoImg) logoImg.src = base64Logo;
    }

    const invoice = document.getElementById("invoiceContent");
    await waitForImages(invoice);

    const canvas = await html2canvas(invoice, { scale: 3, useCORS: true });
    const imgData = canvas.toDataURL("image/png");
    const pdf = new jsPDF("p", "mm", "a4");

    const pageWidth = pdf.internal.pageSize.getWidth();
    const imgProps = pdf.getImageProperties(imgData);
    const pdfHeight = (imgProps.height * pageWidth) / imgProps.width;

    pdf.addImage(imgData, "PNG", 0, 0, pageWidth, pdfHeight);
    pdf.save("invoice.pdf");
  };

  const subtotal = invoiceDetails?.consultation_fee || 0;
  const discount = 0;
  const total = subtotal - discount;


  return (
    <Grid container justifyContent="center">
      <Box
        id="invoiceContent"
        sx={{
          width: "800px",
          p: 4,
          mt: 3,
          backgroundColor: "#fff",
          boxShadow: 3,
          borderRadius: 2,
        }}
      >
        {/* Header */}
        <Typography variant="h5" align="center" fontWeight="bold" mb={5}>
          INVOICE
        </Typography>

        {/* Download Button */}
        <Box
          sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}
          className="icon-hover-actions"
        >
          <Tooltip title="Download">
            <IconButton onClick={handleDownload}>
              <DownloadIcon />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Organisation + Patient Details */}
        <Grid container justifyContent="space-between" alignItems="flex-start" spacing={5}>
          <Grid item>
            {/* Left side: Logo + address */}
            <Box>
              <img
                src={logo}
                alt="Logo"
                style={{ width: "100px" }}
              />
              <Typography variant="body2" mt={1}>
                {invoiceDetails?.branch_name}
              </Typography>
              <Typography variant="body2">
                {invoiceDetails?.address} {invoiceDetails?.country}
              </Typography>
              <Typography variant="body2">
                {invoiceDetails?.city} - {invoiceDetails?.postal_code}
              </Typography>
              <Typography variant="body2">
                {invoiceDetails?.contact_number}
              </Typography>
            </Box>
          </Grid>

          <Grid item>
            {/* Right side: Invoice details */}
            <Box display="flex" flexDirection="column" gap={1} textAlign="right">
              <Box display="flex" justifyContent="space-between" minWidth={150}>
                <Typography variant="body2">Invoice No</Typography>
                <Typography >
                  {invoiceDetails?.invoice_no || "001"}
                </Typography>
              </Box>

              <Box display="flex" justifyContent="space-between" minWidth={150}>
                <Typography variant="body2">Name</Typography>
                <Typography >
                  {invoiceDetails?.name}
                </Typography>
              </Box>

              <Box display="flex" justifyContent="space-between" minWidth={150}>
                <Typography variant="body2">Date</Typography>
                <Typography >
                  {new Date().toLocaleDateString("en-GB")}
                </Typography>
              </Box>

              <Box display="flex" justifyContent="space-between" minWidth={150}>
                <Typography variant="body2">Age</Typography>
                <Typography >
                  {invoiceDetails?.age} Years
                </Typography>
              </Box>
            </Box>
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        {/* Service Table */}
        <TableContainer component={Paper} elevation={0}>
          <Table>
            <TableHead sx={{ backgroundColor: "#f5f5f5" }}>
              <TableRow>
                <TableCell>
                  <strong>Service</strong>
                </TableCell>
                <TableCell align="center">
                  <strong>Qty</strong>
                </TableCell>
                <TableCell align="right">
                  <strong>Rate</strong>
                </TableCell>
                <TableCell align="right">
                  <strong>Amount</strong>
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              <TableRow>
                <TableCell>{invoiceDetails?.description}</TableCell>
                <TableCell align="center">1</TableCell>
                <TableCell align="right">
                  {formatCurrency(subtotal)}
                </TableCell>
                <TableCell align="right">
                  {formatCurrency(subtotal)}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>

        {/* Totals */}
        <Box mt={5} display="flex" justifyContent="flex-end">
          <Box width="300px">
            <Typography
              display="flex"
              justifyContent="space-between"
              variant="body2"
            >
              <span>Subtotal</span>
              <span>{formatCurrency(subtotal)}</span>
            </Typography>

            <Typography
              display="flex"
              justifyContent="space-between"
              variant="body2"
            >
              <span>Discount</span>
              <span>{formatCurrency(discount)}</span>
            </Typography>

            <Divider sx={{ my: 1 }} />

            <Typography
              display="flex"
              justifyContent="space-between"
              variant="h6"
              fontWeight="bold"
            >
              <span>Grand Total</span>
              <span>{formatCurrency(total)}</span>
            </Typography>
          </Box>
        </Box>

        {/* Footer */}
        <Box mt={10} textAlign="center">
          <Typography variant="body2" color="text.secondary">
            Thank you for choosing {invoiceDetails?.branch_name}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            This is a computer-generated invoice.
          </Typography>
        </Box>
      </Box>
    </Grid>
  );
};

export default ConsultationInvoice;